import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  ASSESSMENT_AMENDMENT,
  ASSESSMENT_AMENDMENT_ALL,
  ASSESSMENT_AMENDMENT_ALL_UPDATED,
  ASSESSMENT_AMENDMENT_CREATE,
  ASSESSMENT_AMENDMENT_DELETE,
  ASSESSMENT_AMENDMENT_LIST,
  ASSESSMENT_AMENDMENT_PUBLISH,
  ASSESSMENT_CU_MARK_LIST,
  ASSESSMENT_MARKS_GENERATE,
  ASSESSMENT_MARK_STATUS,
  ASSESSMENT_MODERATOR_FORM,
  ASSESSMENT_MODERATOR_FORM_BULK,
  ASSESSMENT_ZIP_UPLOADS,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const assessmentAmendment = createAsyncThunk(
  ASSESSMENT_AMENDMENT,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendment(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentCreate = createAsyncThunk(
  ASSESSMENT_AMENDMENT_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendmentCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentMarksGenerate = createAsyncThunk(
  ASSESSMENT_MARKS_GENERATE,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_MARKS_GENERATE}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentMarksGenerate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentZipUploads = createAsyncThunk(
  ASSESSMENT_ZIP_UPLOADS,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_ZIP_UPLOADS}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentZipUploads(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentModeratorFormBulk = createAsyncThunk(
  ASSESSMENT_MODERATOR_FORM_BULK,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_MODERATOR_FORM_BULK}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentModeratorFormBulk(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentDelete = createAsyncThunk(
  ASSESSMENT_AMENDMENT_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendmentDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentList = createAsyncThunk(
  ASSESSMENT_AMENDMENT_LIST,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendmentList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentAll = createAsyncThunk(
  ASSESSMENT_AMENDMENT_ALL,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendmentAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentAllUpdated = createAsyncThunk(
  ASSESSMENT_AMENDMENT_ALL_UPDATED,
  async (
    params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT_ALL_UPDATED}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.assessmentAmendmentAllUpdated(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentPublish = createAsyncThunk(
  ASSESSMENT_AMENDMENT_PUBLISH,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_AMENDMENT_PUBLISH}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentAmendmentPublish(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentMarkStatusChange = createAsyncThunk(
  ASSESSMENT_MARK_STATUS,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_MARK_CHANGE_STATUS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentMarkChangeStatus(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentModeratorForm = createAsyncThunk(
  ASSESSMENT_MODERATOR_FORM,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_MODERATOR_FORM}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentModeratorForm(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentCuMarkList = createAsyncThunk(
  ASSESSMENT_CU_MARK_LIST,
  async (params: PayloadCreatorParams<`${typeof API.ASSESSMENT_CU_MARK_LIST}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.assessmentCuMarkList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const assessmentAmendmentClear = createAction(`${ASSESSMENT_AMENDMENT}/clear`)

export const assessmentAmendmentListClear = createAction(`${ASSESSMENT_AMENDMENT_LIST}/clear`)

export const assessmentAmendmentAllClear = createAction(`${ASSESSMENT_AMENDMENT_ALL}/clear`)

export const assessmentAmendmentAllUpdatedClear = createAction(`${ASSESSMENT_AMENDMENT_ALL_UPDATED}/clear`)

export const assessmentMarksGenerateClear = createAction(`${ASSESSMENT_MARKS_GENERATE}/clear`)

export const assessmentZipUploadsClear = createAction(`${ASSESSMENT_ZIP_UPLOADS}/clear`)
